import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import {
    processStrutWaterfallData,
    buildStrutWaterfallChart,
    STRUT_TYPE_COLORS,
    STRUT_TYPE_LABELS,
    STRUT_TYPE_ORDER,
    STRUT_PN_PREFIX,
} from './strutWaterfallUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const USAGE_REMAINING_MAX = 6000;
const USAGE_REMAINING_MIN = 0;
const USAGE_REMAINING_STEP = 50;

const STATUS_COLORS = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bb',
    AWP: '#335d80',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d',
};

function getStatusColor(status) {
    return STATUS_COLORS[status] || '#6c757d';
}

function buildStatusByBuno(records) {
    const map = {};
    (records || []).forEach(ac => {
        if (ac.buno) {
            map[String(ac.buno).trim()] = ac.status || '';
        }
    });
    return map;
}

const getStyles = (theme, embedded) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
    };

    return {
        section: embedded ? {} : {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px',
        },
        card: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: embedded ? '0' : '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        title: {
            fontSize: embedded ? '20px' : '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 8px 0',
        },
        subtitle: {
            fontSize: '14px',
            color: colors.subtleText,
            margin: '0 0 16px 0',
        },
        controls: {
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            fontSize: '14px',
        },
        button: {
            padding: '8px 16px',
            backgroundColor: colors.link,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
        },
        chartContainer: {
            position: 'relative',
            height: '480px',
            margin: '12px 0',
        },
        loading: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
        },
        empty: {
            textAlign: 'center',
            padding: '40px',
            color: colors.subtleText,
            fontStyle: 'italic',
        },
        legendWrap: {
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            marginTop: '16px',
        },
        legendItem: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'opacity 0.15s ease, border-color 0.15s ease',
            font: 'inherit',
            color: colors.text,
        },
        legendItemActive: {
            border: `2px solid ${colors.link}`,
        },
        legendItemInactive: {
            opacity: 0.45,
        },
        legendHint: {
            fontSize: '12px',
            color: colors.subtleText,
            marginTop: '8px',
            fontStyle: 'italic',
        },
        legendSwatch: {
            width: '16px',
            height: '16px',
            borderRadius: '2px',
        },
        detailTable: {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '20px',
            fontSize: '13px',
        },
        tableWrap: {
            maxHeight: '400px',
            overflowY: 'auto',
            marginTop: '8px',
            border: `1px solid ${colors.border}`,
            borderRadius: '4px',
        },
        th: {
            textAlign: 'left',
            padding: '8px',
            borderBottom: `2px solid ${colors.border}`,
            color: colors.text,
        },
        td: {
            padding: '8px',
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
        },
        zoomControl: {
            marginBottom: '16px',
            padding: '14px 16px',
            backgroundColor: isDark ? '#2a2a2a' : '#f8f9fa',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
        },
        zoomHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            flexWrap: 'wrap',
            gap: '8px',
        },
        zoomLabel: {
            fontSize: '14px',
            fontWeight: 'bold',
            color: colors.text,
        },
        zoomValue: {
            fontSize: '14px',
            color: colors.link,
            fontWeight: 'bold',
        },
        zoomSlider: {
            width: '100%',
            height: '6px',
            cursor: 'pointer',
            accentColor: colors.link,
        },
        zoomScale: {
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: colors.subtleText,
            marginTop: '6px',
        },
        zoomHint: {
            fontSize: '12px',
            color: colors.subtleText,
            marginTop: '8px',
            fontStyle: 'italic',
        },
    };
};

function StrutWaterfallPage({ theme, embedded = false }) {
    const styles = getStyles(theme, embedded);
    const [loading, setLoading] = useState(true);
    const [siteFilter, setSiteFilter] = useState('');
    const [sites, setSites] = useState([]);
    const [strutData, setStrutData] = useState([]);
    const [statusByBuno, setStatusByBuno] = useState({});
    const [activeStrutTypes, setActiveStrutTypes] = useState(() => new Set(STRUT_TYPE_ORDER));
    const [usageMax, setUsageMax] = useState(USAGE_REMAINING_MAX);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });

    useEffect(() => {
        api.get('/sites/')
            .then(res => setSites(res.data.results || res.data || []))
            .catch(err => console.error('Error fetching sites:', err));
    }, []);

    const fetchData = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (siteFilter) params.append('site_name', siteFilter);

        params.append('pn__istartswith', STRUT_PN_PREFIX);

        Promise.all([
            api.get(`/crd-all/?${params.toString()}`),
            api.get('/aircraft-status/'),
        ])
            .then(([strutRes, statusRes]) => {
                const data = Array.isArray(strutRes.data) ? strutRes.data : (strutRes.data.results || []);
                setStrutData(data);

                const statuses = Array.isArray(statusRes.data) ? statusRes.data : (statusRes.data.results || []);
                setStatusByBuno(buildStatusByBuno(statuses));
            })
            .catch(err => {
                console.error('Error fetching strut waterfall data:', err);
                setAlert({ show: true, message: 'Failed to fetch strut or aircraft status data.', type: 'error' });
            })
            .finally(() => setLoading(false));
    }, [siteFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const allChartData = useMemo(() => processStrutWaterfallData(strutData), [strutData]);

    const chartData = useMemo(() => {
        const filteredItems = allChartData.items.filter(item => activeStrutTypes.has(item.strutType));
        return buildStrutWaterfallChart(filteredItems, allChartData.strutTypeColors);
    }, [allChartData, activeStrutTypes]);

    const isFullRange = usageMax >= USAGE_REMAINING_MAX;

    const displayedChartData = useMemo(() => {
        if (isFullRange) return chartData;
        const zoomedItems = chartData.items.filter(item => item.remaining <= usageMax);
        return buildStrutWaterfallChart(zoomedItems, chartData.strutTypeColors);
    }, [chartData, usageMax, isFullRange]);

    const zoomedCount = isFullRange ? chartData.items.length : displayedChartData.items.length;

    const legendTypes = useMemo(() => {
        const available = new Set(Object.keys(allChartData.strutTypeColors));
        return STRUT_TYPE_ORDER.filter(type => available.has(type));
    }, [allChartData.strutTypeColors]);

    const toggleStrutType = (type) => {
        setActiveStrutTypes(prev => {
            const next = new Set(prev);
            if (next.has(type)) {
                if (next.size === 1) return prev;
                next.delete(type);
            } else {
                next.add(type);
            }
            return next;
        });
    };

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            title: {
                display: true,
                text: isFullRange
                    ? 'Strut Usage Remaining by Aircraft'
                    : `Strut Usage Remaining by Aircraft (≤ ${usageMax.toLocaleString()} hrs)`,
                font: { size: 16, weight: 'bold' },
                color: theme === 'dark' ? '#e0e0e0' : '#333',
            },
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label(context) {
                        const item = displayedChartData.items[context.dataIndex];
                        if (!item) return '';
                        return [
                            `Usage Remaining: ${item.remaining.toFixed(1)}`,
                            `Part: ${item.pn || 'N/A'}`,
                            `Nomenclature: ${item.nomen || 'N/A'}`,
                            `Scheduled: ${item.schdExpndtr ?? 'N/A'}`,
                        ];
                    },
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'BUNO (Strut Position)',
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                    font: { size: 10 },
                },
                grid: { color: theme === 'dark' ? '#444' : 'rgba(0,0,0,0.1)' },
            },
            y: {
                min: 0,
                ...(isFullRange ? {} : { max: Math.max(usageMax, USAGE_REMAINING_STEP) }),
                title: {
                    display: true,
                    text: 'Usage Remaining',
                    color: theme === 'dark' ? '#e0e0e0' : '#333',
                },
                ticks: { color: theme === 'dark' ? '#e0e0e0' : '#333' },
                grid: { color: theme === 'dark' ? '#444' : 'rgba(0,0,0,0.1)' },
            },
        },
    }), [theme, displayedChartData.items, isFullRange, usageMax]);

    const tableItems = displayedChartData.items;

    const legendControls = legendTypes.length > 0 && (
        <>
            <div style={styles.legendWrap}>
                {legendTypes.map(type => {
                    const colors = allChartData.strutTypeColors[type] || STRUT_TYPE_COLORS.Other;
                    const isActive = activeStrutTypes.has(type);
                    return (
                        <button
                            key={type}
                            type="button"
                            onClick={() => toggleStrutType(type)}
                            style={{
                                ...styles.legendItem,
                                ...(isActive ? styles.legendItemActive : styles.legendItemInactive),
                            }}
                            title={isActive ? `Hide ${STRUT_TYPE_LABELS[type] || type}` : `Show ${STRUT_TYPE_LABELS[type] || type}`}
                        >
                            <div style={{
                                ...styles.legendSwatch,
                                backgroundColor: colors.bg,
                                border: `2px solid ${colors.border}`,
                            }} />
                            <span>{STRUT_TYPE_LABELS[type] || type}</span>
                        </button>
                    );
                })}
            </div>
            <p style={styles.legendHint}>
                Click a legend item to show or hide that strut type. At least one type must remain visible.
            </p>
        </>
    );

    return (
        <div style={styles.section}>
            <CustomAlert
                show={alert.show}
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ show: false, message: '', type: '' })}
            />

            <div style={styles.card}>
                <h2 style={styles.title}>Strut Waterfall</h2>
                <p style={styles.subtitle}>
                    Usage remaining per strut from the CRD All report, filtered to part numbers {STRUT_PN_PREFIX}*. Each bar is one strut (MLG-R, MLG-L, or NLG) sorted by lowest remaining first.
                </p>

                <div style={styles.controls}>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.select}
                    >
                        <option value="">All Sites</option>
                        {sites.map(site => (
                            <option key={site.id} value={site.name}>{site.name}</option>
                        ))}
                    </select>
                    <button type="button" onClick={fetchData} style={styles.button}>
                        Refresh Data
                    </button>
                </div>

                {loading ? (
                    <div style={styles.loading}><Spinner /></div>
                ) : allChartData.items.length === 0 ? (
                    <div style={styles.empty}>
                        No strut part numbers ({STRUT_PN_PREFIX}*) found in CRD All. Upload a CRD All (FOUO CRD) file on the Upload Data page.
                    </div>
                ) : (
                    <>
                        {chartData.items.length === 0 ? (
                            <div style={styles.empty}>
                                No struts match the selected filters. Click a legend item to show that strut type.
                            </div>
                        ) : (
                            <>
                                <div style={styles.zoomControl}>
                                    <div style={styles.zoomHeader}>
                                        <span style={styles.zoomLabel}>Chart zoom — max usage remaining</span>
                                        <span style={styles.zoomValue}>
                                            {isFullRange
                                                ? `${USAGE_REMAINING_MAX.toLocaleString()} (full view)`
                                                : `≤ ${usageMax.toLocaleString()} hrs · ${zoomedCount} strut${zoomedCount === 1 ? '' : 's'}`}
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={USAGE_REMAINING_MIN}
                                        max={USAGE_REMAINING_MAX}
                                        step={USAGE_REMAINING_STEP}
                                        value={usageMax}
                                        onChange={(e) => setUsageMax(Number(e.target.value))}
                                        style={styles.zoomSlider}
                                        aria-label="Chart zoom max usage remaining"
                                    />
                                    <div style={styles.zoomScale}>
                                        <span>0 (zoom in)</span>
                                        <span>{USAGE_REMAINING_MAX.toLocaleString()} (full view)</span>
                                    </div>
                                    <p style={styles.zoomHint}>
                                        Slide left to zoom in and show only struts at or below the selected hours remaining.
                                    </p>
                                </div>

                                {displayedChartData.items.length === 0 ? (
                                    <div style={styles.empty}>
                                        No struts with ≤ {usageMax.toLocaleString()} hrs remaining. Slide right to widen the view.
                                    </div>
                                ) : (
                                    <div style={styles.chartContainer}>
                                        <Bar data={displayedChartData} options={chartOptions} />
                                    </div>
                                )}
                            </>
                        )}

                        {legendControls}

                        {chartData.items.length > 0 && tableItems.length > 0 && (
                            <>
                                <h3 style={{ ...styles.title, fontSize: '16px', marginTop: '24px' }}>
                                    {isFullRange
                                        ? `Strut Detail (${tableItems.length} strut${tableItems.length === 1 ? '' : 's'})`
                                        : `Usage Remaining (≤ ${usageMax.toLocaleString()}) — ${tableItems.length} strut${tableItems.length === 1 ? '' : 's'}`}
                                </h3>
                                <div style={styles.tableWrap}>
                                <table style={styles.detailTable}>
                                    <thead>
                                        <tr>
                                            <th style={styles.th}>BUNO</th>
                                            <th style={styles.th}>Status</th>
                                            <th style={styles.th}>Strut</th>
                                            <th style={styles.th}>Remaining</th>
                                            <th style={styles.th}>Scheduled</th>
                                            <th style={styles.th}>Part Number</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableItems.map(item => {
                                            const acStatus = statusByBuno[String(item.buno).trim()] || '';
                                            return (
                                            <tr key={item.label}>
                                                <td style={styles.td}>{item.buno}</td>
                                                <td style={{
                                                    ...styles.td,
                                                    fontWeight: 'bold',
                                                    color: acStatus ? getStatusColor(acStatus) : styles.td.color,
                                                }}>
                                                    {acStatus || '—'}
                                                </td>
                                                <td style={styles.td}>{item.strutType}</td>
                                                <td style={styles.td}>{item.remaining.toFixed(1)}</td>
                                                <td style={styles.td}>{item.schdExpndtr ?? 'N/A'}</td>
                                                <td style={styles.td}>{item.pn}</td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default StrutWaterfallPage;
export { STRUT_TYPE_COLORS };
